import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Logger } from '@mode/shared/contract-common';
import { INITIATOR_CONTEXT, serializeAndStripIncludes, UriTemplateService } from '@mode/shared/util-halogen';
import { StringHelpers } from '@mode/shared/util-js';
import * as angular from 'angular';
import _ from 'lodash';
import { Resource } from '@mode/shared/util-halogen';
import { ModeResourceFactoryService } from '../api/mode-resource.factory';
import { QAdapterService } from '../migration/q-adapter.service';
import { ModelCache } from '../model-cache/model-cache';
import { UrlExpanderService } from '../url-expander/url-expander.service';
import { extend } from './extend';
import { ModeModelBase } from './model';
import { ModelNameService } from './model-name.service';

export class ModeCollection {
  _name = 'collection';

  _model = 'model';

  _allowedIdParams = ['filter', 'perspective'];
  private _resource: Resource;
  id: any;
  initialize: any;

  constructor(
    private $log: Logger,
    private modelNameService: ModelNameService,
    private modeCollectionFactory: ModeCollectionFactoryService,
    private $q: QAdapterService,
    resource: Resource,
    model,
    parent,
    modelRelationName,
    modelRelationAsName
  ) {
    this._resource = resource;
    this._model = model ? model : this._model;

    if (typeof this._resource !== 'undefined' && this._resource.hasLink('self')) {
      this.id = this._getIdFromLink(this._resource.links('self').href);
    } else {
      this.$log.warn('collection has no id set for ', this._model);
    }

    this._initEmbeddedCollection(
      modelRelationName || StringHelpers.pluralize(this._model),
      modelRelationAsName,
      parent
    );

    if (typeof this.initialize === 'function') {
      this.initialize.call(this, this._resource);
    }

    if (this._resource) {
      this.$log.debug('created collection', this);
    }
  }

  _initEmbeddedCollection(name, relationAs, parent) {
    const embedded_name = StringHelpers.underscore(name);
    const embedded_relation = StringHelpers.underscore(relationAs);

    if (
      !this._resource ||
      typeof this._resource.hasEmbedded !== 'function' ||
      (!this._resource.hasEmbedded(embedded_name) && !this._resource.hasEmbedded(embedded_relation))
    ) {
      this.$log.debug('no collection embedded in ' + name);
      return;
    }

    const collection = this._resource.embedded(embedded_name) || this._resource.embedded(embedded_relation),
      model = this.modelNameService.get(this._model);

    if (typeof model === 'undefined') {
      this.$log.warn('Cannot intialize unknown collection model ' + this._model + ' in ' + name);
      return;
    }

    angular.forEach(
      collection,
      (resource, index) => {
        const newModel = model.createModel(resource);
        if (parent) {
          newModel.parent = parent[parent.idAttribute];
        }
        newModel._collections = [{ id: this.id, position: index }];
        model.putLocal(newModel);
      },
      this
    );
  }

  _getIdFromLink(link) {
    const urlParts = link.split('?');
    const baseUrl = urlParts[0];
    const queryParams = urlParts.length > 1 ? urlParts[1] : null;
    const allowedParams = [];

    if (queryParams) {
      const params = queryParams.split('&');
      for (let i = 0; i < params.length; i++) {
        const keyValue = params[i].split('=');
        if (this._allowedIdParams.indexOf(keyValue[0]) >= 0) {
          allowedParams.push(keyValue[0] + '=' + keyValue[1]);
        }
      }
    }

    if (allowedParams.length) {
      return baseUrl + '?' + allowedParams.join('&');
    } else {
      return baseUrl;
    }
  }

  extendAttributes(collection) {
    collection._resource._links && angular.extend(this._resource._links, collection._resource._links);
    collection._resource._embedded && angular.extend(this._resource._embedded, collection._resource._embedded);
    collection._resource._forms && angular.extend(this._resource._forms, collection._resource._forms);

    if (typeof this.initialize === 'function') {
      this.initialize.call(this, this._resource);
    }
  }

  elements() {
    const model = this.modelNameService.get(this._model);
    const opts = {
      where: {
        _collections: (collections) => {
          return _.some(collections, { id: this.id });
        },
      },
      sort: (model) => {
        const collection = _.find(model._collections, { id: this.id });
        return collection ? collection.position : undefined;
      },
    };
    return model.findAll(opts);
  }

  watch(scope, handler) {
    const model = this.modelNameService.get(this._model);

    return scope.$watchCollection(
      () => {
        return model.cache.all();
      },
      (newCollection) => {
        this.$q
          .all(newCollection)
          .then((models: any) => {
            const elements = _.chain(models)
              .filter((model) => {
                return _.some(model._collections, { id: this.id });
              })
              .sortBy((model) => {
                const collection = _.find(model._collections, { id: this.id });
                return collection ? collection.position : undefined;
              })
              .value();
            handler.call(this, elements);
          })
          .catch((e) => {
            console.warn('failed to get watch collection ' + this._model, e);
          });
      }
    );
  }

  next(params) {
    if (this._resource.links('next_page')) {
      const deferred = this.$q.defer();
      this.modeCollectionFactory
        .find(this._resource.links('next_page').href, this._model, params)
        .then((collection) => {
          // update current collections next cursor
          this._resource._links.next_page = collection._resource.links.next_page;
          deferred.resolve(collection);
        })
        .catch(deferred.reject);
      return deferred.promise;
    } else {
      return this.$q.reject(new Error('No next link to collection ' + this._model + ' found'));
    }
  }

  hasNext() {
    return this._resource.hasLink('next_page');
  }

  reload() {
    if (typeof this._resource.hasLink === 'function' && this._resource.hasLink('self')) {
      const deferred = this.$q.defer();
      this._resource
        .links('self')
        .$follow()
        .then((resource) => {
          const collection = this.modeCollectionFactory.create(resource, this._model);
          this.modeCollectionFactory.put(collection).then(deferred.resolve).catch(deferred.reject);
        });
      return deferred.promise;
    } else {
      return this.$q.reject(new Error('No link to collection ' + this._model + ' found'));
    }
  }

  /*
   * insert model in collection at position (adjusting all positions after it)
   * or at the end if position is not defined
   */
  insert(model, position) {
    const deferred = this.$q.defer();
    const testPosition = typeof position === 'number' ? position : Number.MAX_VALUE;
    let maxPosition = -1;
    const modelAccessor = this.modelNameService.get(this._model);
    modelAccessor
      .findAll({
        where: {
          _collections: (collections) => {
            return _.some(collections, (collection) => {
              // keep track of max position in collection
              if (collection.id === this.id) {
                maxPosition = Math.max(collection.position, maxPosition);
              }
              // return all models over the passed position
              return collection.id === this.id && collection.position >= testPosition;
            });
          },
        },
      })
      .then((modelsToMove: ModeModelBase[]) => {
        maxPosition = Math.max(maxPosition + 1, 0);
        // final position is either the passed position or the end
        const finalPosition = typeof position === 'number' ? Math.min(position, maxPosition) : maxPosition;
        // update all positions after
        _.each(modelsToMove, (modelToMove) => {
          const collection = _.find(modelToMove._collections, { id: this.id });
          if (collection && typeof collection.position === 'number') {
            collection.position += 1;
          }
        });
        if (model._collections) {
          const currentCollection = _.find(model._collections, { id: this.id });
          if (currentCollection) {
            currentCollection.position = finalPosition;
          } else {
            model._collections.push({ id: this.id, position: finalPosition });
          }
        } else {
          model._collections = [{ id: this.id, position: finalPosition }];
        }
        deferred.resolve(model);
      })
      .catch(deferred.reject);

    return deferred.promise;
  }

  remove(model) {
    if (model._collections) {
      _.remove(model._collections, { id: this.id });
    }
  }
}

@Injectable({ providedIn: 'root' })
export class ModeCollectionFactoryService {
  cache: ModelCache.Cache;

  constructor(
    modelCache: ModelCache,
    private $log: Logger,
    private modelNameService: ModelNameService,
    private $http: HttpClient,
    private modeResourceFactory: ModeResourceFactoryService,
    private urlExpander: UrlExpanderService,
    private $q: QAdapterService,
    private uriTemplateService: UriTemplateService
  ) {
    this.cache = modelCache.getOrCreate('mode.collection');
  }

  create(resource, model, parent?, modelRelationName?, modelRelationAsName?) {
    return new ModeCollection(
      this.$log,
      this.modelNameService,
      this,
      this.$q,
      resource,
      model,
      parent,
      modelRelationName,
      modelRelationAsName
    );
  }

  extend = extend;

  find(id, model, params?) {
    const cache = this.cache;

    let includes;
    if (params && params.includes) {
      includes = serializeAndStripIncludes(params);
    }

    if (!includes && (!params || !params.refresh) && cache && cache.get(id)) {
      return cache.get(id);
    }

    const url = this.uriTemplateService.parse(this.urlExpander.expand(id)).expand(params);
    const deferred = this.$q.defer();

    this.$http
      .get(url, {
        params: includes,
        context: new HttpContext().set(INITIATOR_CONTEXT, new Error()),
      })
      .toPromise()
      .then(
        (body) => {
          const resource = this.modeResourceFactory.create(body);
          const collection = this.create(resource, model);
          deferred.resolve(collection);
        },
        (errorResponse) => {
          if ('status' in errorResponse) {
            deferred.reject(new Error(errorResponse.message));
          } else {
            deferred.reject(errorResponse);
          }
        }
      );

    cache && cache.put(id, deferred.promise);

    return deferred.promise;
  }

  put(collection) {
    const deferred = this.$q.defer();
    const cache = this.cache;
    if (cache) {
      if (cache.get(collection.id)) {
        cache
          .get(collection.id)
          .then((oldCollection) => {
            oldCollection.extendAttributes(collection);
            //this probably doesnt apply to collections
            //delete oldCollection['_new_' + collection._name];
            deferred.resolve(oldCollection);
          })
          .catch((error) => {
            if (typeof error !== 'undefined' && error.status === 404) {
              // one case where we want to override old failed promise
              cache.put(collection.id, deferred.promise);
              deferred.resolve(collection);
            } else {
              deferred.reject(error);
            }
          });
      } else {
        if (typeof collection.id !== 'undefined') {
          cache.put(collection.id, deferred.promise);
        } else {
          this.$log.warn('Could not save collection in cache - no id specified');
        }
        deferred.resolve(collection);
      }
    } else {
      deferred.resolve(collection);
    }
    return deferred.promise;
  }

  elements(collection) {
    return collection.elements();
  }

  reload(collection) {
    return collection.reload();
  }

  extendModelCollections(oldCollections, newCollections) {
    //override old collections that have updated
    const extendedCollections = _.map(oldCollections, (collection) => {
      return _.find(newCollections, { id: collection.id }) || collection;
    });
    //then include any new collections
    return _.union(extendedCollections, newCollections);
  }
}

/**
 * Adapter to help migrate to Angular 2 dependency injector.
 * Migration Guide: If you're migrating a service that depends on this, update to ModeCollectionFactoryService.create first.
 *
 * @deprecated Use ModeCollectionFactoryService instead
 */
export function modeCollectionFactoryAngularMigrationAdapter(modeCollectionFactory: ModeCollectionFactoryService) {
  // Doing a funy thing as a bridge for angularjs. Maybe there's a more clever way to handle this, but creating this wrapper
  // object seems to work.

  // Create the factory function
  const ModeCollectionFactory: any = function (resource, model, parent?, modelRelationName?, modelRelationAsName?) {
    return modeCollectionFactory.create(resource, model, parent, modelRelationName, modelRelationAsName);
  };
  // Bind all the other factory methods to this object
  ModeCollectionFactory.extend = modeCollectionFactory.extend.bind(modeCollectionFactory);
  ModeCollectionFactory.find = modeCollectionFactory.find.bind(modeCollectionFactory);
  ModeCollectionFactory.put = modeCollectionFactory.put.bind(modeCollectionFactory);
  ModeCollectionFactory.elements = modeCollectionFactory.elements.bind(modeCollectionFactory);
  ModeCollectionFactory.reload = modeCollectionFactory.reload.bind(modeCollectionFactory);
  ModeCollectionFactory.extendModelCollections =
    modeCollectionFactory.extendModelCollections.bind(modeCollectionFactory);

  return ModeCollectionFactory;
}

modeCollectionFactoryAngularMigrationAdapter.$inject = ['ModeCollectionFactory'];
